import React, { useState } from 'react';
import Modal from './Modal';
import StatusBadge from './StatusBadge';
import Timeline from './Timeline';
import { reportService } from '../services/api';

const ReportDetailsModal = ({ report, isOpen, onClose, onStatusUpdate, onFileView }) => {
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('pending');
  const [notes, setNotes] = useState('');
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [fullReport, setFullReport] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isRefreshingTimeline, setIsRefreshingTimeline] = useState(false);

  // Fetch full report details including status history when modal opens
  React.useEffect(() => {
    const fetchFullReportDetails = async () => {
      if (report && isOpen && report.reference_id) {
        setIsLoadingDetails(true);
        try {
          const response = await reportService.getReport(report.reference_id);
          if (response.data?.success) {
            setFullReport(response.data.data);
            console.log('Full report details loaded:', response.data.data);
          }
        } catch (error) {
          console.error('Error fetching full report details:', error);
          // Fallback to using the original report if API fails
          setFullReport(report);
        } finally {
          setIsLoadingDetails(false);
        }
      }
    };

    fetchFullReportDetails();
  }, [report, isOpen]);

  // Update selectedStatus when report changes
  React.useEffect(() => {
    const currentReport = fullReport || report;
    if (currentReport?.status) {
      setSelectedStatus(currentReport.status);
    }

    // Debug: Log report structure to console
    if (currentReport) {
      console.log('Status history:', currentReport.status_history);
    }
  }, [fullReport, report]);

  if (!report) return null;

  // Use full report details if available, otherwise fall back to the original report
  const currentReport = fullReport || report;

  const statusOptions = [
    { value: 'pending', label: 'Pending', description: 'Report has been submitted and is awaiting review' },
    { value: 'under_investigation', label: 'Under Investigation', description: 'Report is currently being investigated' },
    { value: 'resolved', label: 'Resolved', description: 'Issue has been resolved and case is closed' },
    { value: 'dismissed', label: 'Dismissed', description: 'Report was invalid or could not be verified' }
  ];

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    setIsUpdatingStatus(true);
    try {
      await onStatusUpdate(currentReport.id, selectedStatus, notes);

      // Refresh the full report data to get updated status history
      setIsRefreshingTimeline(true);
      if (currentReport.reference_id) {
        const response = await reportService.getReport(currentReport.reference_id);
        if (response.data?.success) {
          setFullReport(response.data.data);
          console.log('Report refreshed after status update:', response.data.data);
        }
      }

      setShowStatusForm(false);
      setNotes('');
      // Show success notification
      if (window.showToast) {
        window.showToast(`Report status updated to "${selectedStatus}"`, 'success');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      if (window.showToast) {
        window.showToast('Failed to update status. Please try again.', 'error');
      }
    } finally {
      setIsUpdatingStatus(false);
      setIsRefreshingTimeline(false);
    }
  };

  const resetStatusForm = () => {
    setSelectedStatus(currentReport?.status || 'pending');
    setNotes('');
    setShowStatusForm(false);
  };

  const handleFileView = (file) => {
    if (onFileView) {
      onFileView(file);
    }
  };

  const handleFileDownload = async (file) => {
    try {
      // Use the same API endpoint pattern as for file viewing
      const token = localStorage.getItem('ncrs_token');
      console.log('Download token check:', token ? 'Token found' : 'No token found');
      console.log('File object:', file);

      // Extract file ID from the file URL or use file properties
      let fileId = null;

      console.log('File URL:', file.url);
      console.log('File ID property:', file.id);

      // Try to extract file ID from URL pattern
      if (file.url && file.url.includes('/files/')) {
        const urlParts = file.url.split('/files/');
        fileId = urlParts[1]?.split('/')[0];
        console.log('Extracted file ID from URL:', fileId);
      } else if (file.id) {
        fileId = file.id;
        console.log('Using direct file ID:', fileId);
      }

      if (!fileId) {
        console.error('Could not determine file ID for download:', file);
        if (window.showToast) {
          window.showToast('Unable to download file - invalid file reference', 'error');
        }
        return;
      }

      // Instead of constructing URL, try to use the file.url directly first
      let downloadUrl;
      if (file.url && file.url.startsWith('http')) {
        // Use the URL directly if it's a full URL
        downloadUrl = file.url;
        console.log('Using direct file URL:', downloadUrl);
      } else {
        // Construct the API endpoint URL
        downloadUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/files/${fileId}`;
        console.log('Constructed download URL:', downloadUrl);
      }

      // Show loading state
      if (window.showToast) {
        window.showToast('Downloading file...', 'info');
      }

      // Create headers object - include auth if token exists
      const headers = {
        'Accept': '*/*'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Create a temporary link and trigger download with proper headers
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: headers
      });      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }      // Get the blob data
      const blob = await response.blob();

      // Determine filename
      const downloadFilename = file.filename || file.original_name || file.name || 'download';

      // Create blob URL and trigger download (without target='_blank')
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadFilename;
      link.style.display = 'none'; // Hide the link
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);

      if (window.showToast) {
        window.showToast('File downloaded successfully', 'success');
      }
    } catch (error) {
      console.error('Download error:', error);
      console.log('Trying fallback download method...');

      // Fallback: Try using the file URL directly if primary method fails
      if (file.url && error.message.includes('404')) {
        try {
          // Try direct URL access
          const fallbackResponse = await fetch(file.url, {
            method: 'GET',
            headers: {
              'Accept': '*/*'
            }
          });

          if (fallbackResponse.ok) {
            const blob = await fallbackResponse.blob();
            const downloadFilename = file.filename || file.original_name || file.name || 'download';

            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = downloadFilename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(blobUrl);

            if (window.showToast) {
              window.showToast('File downloaded successfully (fallback)', 'success');
            }
            return; // Success with fallback
          }
        } catch (fallbackError) {
          console.error('Fallback download also failed:', fallbackError);
        }
      }

      if (window.showToast) {
        window.showToast(`Failed to download file: ${error.message}`, 'error');
      }
    }
  };  const getFileIcon = (filename) => {
    const extension = filename?.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
      return '🖼️';
    } else if (extension === 'pdf') {
      return '📄';
    }
    return '📎';
  };

  const getFileSize = (size) => {
    if (!size) return 'Unknown size';
    if (typeof size === 'string') return size;

    const bytes = parseInt(size);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Report Details - ${currentReport.reference_id}`}
      size="xl"
    >
      <div className="space-y-6">
        {/* Basic Information and Complainant Information in Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-3">Basic Information</h4>
            <div className="space-y-3">

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-500">Reference ID</label>
                  <p className="text-gray-900">{currentReport.reference_id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">
                    <StatusBadge status={currentReport.status} />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Contractor Name</label>
                <p className="text-gray-900">{currentReport.contractor_name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Project Location</label>
                <p className="text-gray-900">{currentReport.project_location}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-500">Report Date</label>
                  <p className="text-gray-900">{new Date(currentReport.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Last Updated</label>
                  <p className="text-gray-900">
                    {currentReport.updated_at ? new Date(currentReport.updated_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

            </div>
          </div>

          {/* Complainant Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-3">Complainant Information</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Name</label>
                <p className="text-gray-900">
                  {(currentReport.complainant && currentReport.complainant.name) ||
                   currentReport.complainant_name ||
                   'Anonymous'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Email</label>
                <p className="text-gray-900">
                  {(currentReport.complainant && currentReport.complainant.email) ||
                   currentReport.complainant_email ||
                   'Not provided'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Phone</label>
                <p className="text-gray-900">
                  {(currentReport.complainant && currentReport.complainant.phone) ||
                   currentReport.complainant_phone ||
                   'Not provided'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Privacy Consent</label>
                <p className="text-gray-900">
                  {currentReport.data_privacy_consent ? 'Yes' : 'No'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Report Details */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-3">Report Description</h4>
          <p className="text-gray-700 whitespace-pre-wrap">
            {currentReport.report_details || currentReport.description || 'This is a mock report detail. In the actual system, this would contain the full description of the issue reported by the citizen.'}
          </p>
        </div>

        {/* Attachments */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-3">Attachments</h4>
          <div className="space-y-2">
            {(() => {
              // Try multiple possible file field names
              const files = currentReport.files || currentReport.report_files || currentReport.attachments || [];
              console.log('Found files:', files);

              return files && files.length > 0 ? (
                files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">{getFileIcon(file.original_name || file.filename || file.name)}</span>
                      <div>
                        <button
                          onClick={() => handleFileView({
                            filename: file.original_name || file.filename || file.name,
                            url: file.url,
                            size: file.human_file_size || file.file_size
                          })}
                          className="text-blue-600 hover:text-blue-800 font-medium text-left"
                        >
                          {file.original_name || file.filename || file.name || 'Unnamed file'}
                        </button>
                        <p className="text-gray-500 text-sm">{file.human_file_size || getFileSize(file.file_size)}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleFileView({
                          filename: file.original_name || file.filename || file.name,
                          url: file.url,
                          size: file.human_file_size || file.file_size
                        })}
                        className="text-blue-600 hover:text-blue-800 p-2"
                        title="View file"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleFileDownload({
                          filename: file.original_name || file.filename || file.name,
                          url: file.url,
                          id: file.id
                        })}
                        className="text-gray-600 hover:text-gray-800 p-2"
                        title="Download file"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">No attachments found</p>
                  <p className="text-xs text-gray-400">Files will appear here when available</p>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Status Update Section */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">Update Status</h4>
            {/* Prevent status changes for resolved and dismissed reports */}
            {!['resolved', 'dismissed'].includes(currentReport.status) ? (
              <button
                onClick={() => setShowStatusForm(!showStatusForm)}
                className="text-primary hover:text-blue-700 font-medium"
              >
                {showStatusForm ? 'Cancel' : 'Change Status'}
              </button>
            ) : (
              <span className="text-sm text-gray-500 italic">
                Status cannot be changed for {currentReport.status} reports
              </span>
            )}
          </div>

          {showStatusForm && !['resolved', 'dismissed'].includes(currentReport.status) ? (
            <form onSubmit={handleStatusUpdate} className="space-y-4">
              {/* Status Options */}
              <div>
                <label className="form-label">Select New Status</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {statusOptions.map((option) => (
                    <div key={option.value}>
                      <input
                        type="radio"
                        name="status"
                        value={option.value}
                        id={`status-${option.value}`}
                        checked={selectedStatus === option.value}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="sr-only"
                      />
                      <label
                        htmlFor={`status-${option.value}`}
                        className={`
                          flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all duration-200
                          ${selectedStatus === option.value
                            ? 'border-blue-500 bg-blue-50 shadow-md'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                          }
                        `}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">{option.label}</span>
                          <div className="flex items-center space-x-2">
                            <StatusBadge status={option.value} size="sm" />
                            <div className={`
                              w-4 h-4 rounded-full border-2 flex items-center justify-center
                              ${selectedStatus === option.value
                                ? 'border-blue-500 bg-blue-500'
                                : 'border-gray-300'
                              }
                            `}>
                              {selectedStatus === option.value && (
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                              )}
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 text-left">{option.description}</p>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="form-label">Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes or comments about this status change..."
                  className="form-input h-20 resize-none"
                />
              </div>

              {/* Submit Button */}
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={isUpdatingStatus || selectedStatus === currentReport.status}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdatingStatus ? 'Updating...' : 'Update Status'}
                </button>
                <button
                  type="button"
                  onClick={resetStatusForm}
                  className="border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">Current Status:</span>
              <StatusBadge status={currentReport.status} />
            </div>
          )}
        </div>

        {/* Timeline Section */}
        <div className="bg-white p-4 rounded-lg border">
          {isLoadingDetails || isRefreshingTimeline ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              <p className="text-sm text-gray-500 mt-2">
                {isRefreshingTimeline ? 'Updating timeline...' : 'Loading timeline...'}
              </p>
            </div>
          ) : (
            <Timeline statusHistory={currentReport.status_history || []} />
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-4 border-t">
          <button
            onClick={async () => {
              try {
                const response = await reportService.exportReportPdf(currentReport.reference_id);

                if (response && response.data) {
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `report_${currentReport.reference_id}_${new Date().toISOString().split('T')[0]}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                } else {
                  throw new Error('Export failed');
                }
              } catch (error) {
                console.error('Error exporting report:', error);
                alert('Failed to export report. Please try again.');
              }
            }}
            className="border border-primary text-primary px-4 py-2 rounded hover:bg-gray-50"
          >
            Export PDF
          </button>

        </div>
      </div>
    </Modal>
  );
};

export default ReportDetailsModal;
